
import Vue from 'vue';
import type { PropType } from 'vue';

import { IDateTimeSearch } from '@/types/misc_data';
import { createPlaceholderImage, dateToAustrianTimezone } from '@/_helpers/misc_helper';
import { Site, Truck, Model, ITruckGroupBySize } from '~/types/truck';

import SlotGridNew from '@/components/general/calendar/SlotGridNew.vue';
import TruckInfoNew from '@/components/truck/TruckInfoNew.vue';
import OpeningHoursDialog from '@/components/map/OpeningHoursDialog.vue';

import CART from '@/store/modules/CartModule';
import MAP from '@/store/modules/MapModule';
import TRUCKS from '@/store/modules/TruckModule';
import UTILS from '@/store/modules/UtilityModule';
import MISC_DATA from '@/store/modules/MiscDataModule';
import BOOKING from '@/store/modules/BookingModule';
import { ErrorResponse } from '~/types/api_helper';
import { ITruckClickPayLoad, ISlotCLickedOrConfirmedPayload } from '~/plugins/tracking';
import { EventBus } from '@/utils/eventBus';

interface StringIndex {
	[key: string]: any;
}

interface BlockedSlotIndexes {
	non_working_slots: number[];
	[key: string]: any;
}

export default Vue.extend({
	name: 'MapTruckCardNew',

	components: { TruckInfoNew, SlotGridNew, OpeningHoursDialog },

	props: {
		truckId: {
			type: String,
			required: true
		},
		truckLocation: {
			type: String,
			default: ''
		},
		truckCounter: {
			type: String,
			default: ''
		},
		truckAddress: {
			type: String,
			default: ''
		},
		isSingleTruck: {
			type: Boolean,
			default: false
		},
		trucks: {
			type: Object as PropType<ITruckGroupBySize>,
			default: null
		},
		truckList: {
			type: Array as PropType<Truck[]>,
			default: null
		},
		resetCalendarPage: {
			type: Number,
			default: 0
		},
		isChooseSize: {
			type: Boolean,
			default: false
		},
		truckAvailable: Boolean,
		hideCloseButton: Boolean,
		defaultToDetailsTab: Boolean,
		hideCycling: Boolean,
		truckQuery: Boolean,
		disableLeftButton: Boolean,
		autoselectFirstSlots: Boolean,
		triggerClick: {
			type: Object as PropType<{
				triggerClick: Number;
				triggerId: String;
			}>,
			default: () => {
				return { triggerClick: 0, triggerId: '' };
			}
		},
		showDetailsTab: { type: Number, default: 0 },
		allTrucksInDialog: {
			type: Array as PropType<Truck[]>,
			default: null
		},
		updateTruckImg: {
			type: String,
			default: ''
		},
		allSlotsInDialog: {
			type: Array as PropType<{ truck_id: string; slots: number[] | ErrorResponse }[]>,
			default: () => []
		},
		singleTruckData: {
			type: Object as () => Truck | null,
			default: null
		}
	},

	data() {
		return {
			truck: null as Truck | null,
			tab: 0,
			calendar: {
				start: '',
				end: ''
			},
			calendar_auto_select: false,
			peaktime_selected: false,
			peaktime2_selected: false,
			offtime_selected: false,
			loader: false,
			rent_btn: {
				price: 0,
				loading: true
			},
			selectedTruckSize: 'xxl',
			currentImagePath: '',
			isOpen: false,
			errorMsgs: ['START_DAY_CLOSED', 'START_BEFORE_OPENING_HOURS', 'END_DAY_CLOSED', 'END_AFTER_OPENING_HOURS', 'END_BEFORE_OPENING_HOURS'],
			currentPage: '' as string,
			pageNumber: 0 as number,
			openingHoursDialogExtend: false,
			openingHoursDialogCannotExtend: false,
			extendedSlotsData: {
				slots: [],
				startTime: '',
				endTime: '',
				price: 0
			} as any,
			currentWeekDates: [] as string[]
		};
	},

	computed: {
		CART: () => CART,
		MAP: () => MAP,
		UTILS: () => UTILS,
		TRUCKS: () => TRUCKS,
		MISC_DATA: () => MISC_DATA,
		BOOKING: () => BOOKING,

		unavailableSlots(): number[] {
			const currentSlotItem = this.allSlotsInDialog.find((item) => item.truck_id === this.truckId);
			if (currentSlotItem && Array.isArray(currentSlotItem.slots)) {
				return currentSlotItem.slots;
			}
			return [];
		},

		isMobile(): boolean {
			return this.$vuetify.breakpoint.smAndDown;
		},

		singleTruckBranded(): boolean {
			const site = this.singleTruckData?.site;
			if (site && typeof site !== 'string') {
				return site.branding?.enabled === true;
			}
			return false;
		},

		singleTruckBrandData(): any {
			const site = this.singleTruckData?.site;
			if (site && typeof site !== 'string') {
				return site.branding;
			}
			return false;
		},

		carouselTruckBranded(): boolean {
			const site = this.getCurrentTruck?.site;
			if (site && typeof site !== 'string') {
				return site.branding?.enabled === true;
			}
			return false;
		},

		carouselTruckBrandData(): any {
			const site = this.getCurrentTruck?.site;
			if (site && typeof site !== 'string') {
				return site.branding;
			}
			return false;
		},

		/*    truck(): any {
	  return TRUCKS.all_trucks.find(item => item.id === this.truckId);
	},
*/
		timePicked(): boolean {
			const dateTimePicked = this.CART.last_time_search as IDateTimeSearch;
			return Object.values(dateTimePicked).every((value) => !!value);
		},

		startTime(): string {
			if (this.timePicked) {
				// Timepicker
				return this.CART.last_time_search.start as string;
			}

			if (this.calendar.start) {
				return this.calendar.start;
			}

			return '';
		},

		endTime(): string {
			if (this.timePicked) {
				return this.CART.last_time_search.end as string;
			}

			if (this.calendar.end) {
				return this.calendar.end;
			}

			return '';
		},

		truckIsAvailable(): boolean {
			if (!this.truck || this.truckAvailable === undefined) {
				return false;
			}
			// truckAvailable comes from truck carousel and passed down availability since we don't have it in the truck response
			return this.truckAvailable;
		},

		isInTruckCarousel(): boolean {
			return !!this.$route.query.dialog && this.$route.query.dialog.includes('carousel');
		},

		truckLocationId(): string | null {
			if (this.truckLocation) {
				return this.truckLocation;
			}
			if (this.truck && this.truck.site instanceof Site) {
				return this.truck.site.id;
			}

			return null;
		},

		getVcardMinHeight(): number | undefined {
			if (this.timePicked) {
				if (this.isSingleTruck) {
					return 400;
				}
				return 200;
			}

			if (!this.timePicked) {
				if (this.isSingleTruck) {
					return 850;
				}
				if (this.isChooseSize && this.tab === 1) {
					return 670;
				}
				return 650;
			}

			return undefined;
		},

		getVcarouselMinHeight(): string {
			if (this.$vuetify.breakpoint.smAndUp) {
				if (this.isSingleTruck) {
					return '225px';
				}
				let promoText = '';
				if (this.carouselTruckBranded && this.carouselTruckBrandData?.text) {
					const div = document.createElement('div');
					div.innerHTML = this.carouselTruckBrandData.text;
					promoText = div.textContent || div.innerText || '';
				}
				return promoText.length > 60 ? '50px' : '30px';
			}

			if (!this.$vuetify.breakpoint.smAndUp) {
				if (this.isSingleTruck) {
					return '200px';
				}
				let promoText = '';
				if (this.carouselTruckBranded && this.carouselTruckBrandData?.text) {
					const div = document.createElement('div');
					div.innerHTML = this.carouselTruckBrandData.text;
					promoText = div.textContent || div.innerText || '';
				}
				return promoText.length > 60 ? '50px' : '30px';
			}

			return '225px';
		},

		imgList(): string[] | null {
			const imgs = this.truck?.pictures;
			const pattern = {
				front_driver: 0,
				co_driver_open: 1,
				front: 2,
				co_driver: 3,
				back: 4,
				driver: 5,
				drivers_cab: 6,
				driver_open: 7,
				back_open: 8
			} as { [index: string]: any };

			if (!imgs) {
				return null;
			}

			const imgsKeys = Object.keys(imgs);
			imgsKeys.sort((a: any, b: any) => {
				// Sort the keys following the pattern if duplicate they'll follow each other if not in the pattern they will be at the end
				return pattern[a] - pattern[b];
			});
			const pictures = [] as any;
			for (const i in imgsKeys) {
				if (imgs[imgsKeys[i]]) {
					pictures.push(imgs[imgsKeys[i]]);
				}
			}
			return pictures;
		},

		placeholderImage(): string {
			if (process.server) {
				return '';
			}
			return createPlaceholderImage({
				width: 600,
				text: this.$t('truck.pictures.none') as string
			});
		},

		truckName(): string | undefined {
			if (!this.truck) {
				return;
			}
			const model = this.truck.model as Model;
			return `${model.manufacturer} ${model.model}`;
		},

		truckNumber(): string {
			const matches = this.truck?.name.match(/#\w+/g);
			if (matches && matches.length > 0 && matches[0].includes('#')) {
				return matches[0];
			}
			return '';
		},

		promoOneSlotIsActive(): boolean {
			if (!this.MISC_DATA.basedata.features) {
				return false;
			}
			return this.MISC_DATA.basedata.features['6_hour_blocks'] === true;
		},

		truckMinPrice6(): number {
			if (!(this.truck instanceof Truck)) {
				return 0;
			}

			if (this.truck['min-price'].price_6 > 0) {
				return this.truck['min-price'].price_6;
			}

			// fallback old logic

			const prices = this.MISC_DATA.prices;
			const size = (this.truck?.model as Model).size_group.toLowerCase();

			switch (size) {
				case 'l':
					return prices.truck1_6off;
				case 'xl':
					return prices.truck2_6off;
				case 'xxl':
					return prices.truck3_6off;
			}

			return 0;
		},

		truckMinPrice24(): number {
			if (!(this.truck instanceof Truck)) {
				return 0;
			}

			if (this.truck['min-price'].price_24 > 0) {
				return this.truck['min-price'].price_24;
			}

			// fallback old logic

			if (!(this.truck.model instanceof Model)) {
				return 0;
			}

			const size = {
				L: 'truck1',
				XL: 'truck2',
				XXL: 'truck3'
			} as StringIndex;

			const price_key = size[this.truck.model.size_group];

			return MISC_DATA.prices[price_key];
		},

		bookingBtnText(): string {
			return this.$t('actions.book_now_promo', {
				price: this.$n(this.rent_btn.price, this.isInt(this.rent_btn.price) ? 'currencyShort' : 'currency')
			}) as string;
		},

		mainImage(): string {
			if (!(this.truck instanceof Truck)) {
				return '';
			}

			const size = (this.truck?.model as Model).size_group.toLowerCase();

			switch (size) {
				case 'l':
					return this.staticAssetsPath + '/img/truck_mockups/new-images/new-l.png';
				case 'xl':
					return this.staticAssetsPath + '/img/truck_mockups/new-images/new-xl.png';
				case 'xxl':
					return this.staticAssetsPath + '/img/truck_mockups/new-images/new-xxl.png';
			}

			return '';
		},

		imageFromBe(): string | null | undefined {
			return this.singleTruckData?.pictures?.mockup_side;
		},

		getCurrentTruck(): any {
			if (!this.isSingleTruck) {
				return this.allTrucksInDialog.find((item) => item.id === this.truckId) || null;
			}
			return null;
		},

		singleTruckAddress(): any {
			// @ts-ignore
			const address = this.singleTruckData?.site?.address;
			return address.length > 40 ? address.slice(0, 40) + ' ...' : address;
		},

		showTruckAddress(): string {
			// @ts-ignore
			return this.truckAddress || this.singleTruckData?.site?.address;
		},

		getWorkingHours(): any {
			// Use type assertion to handle TypeScript errors
			const truck = this.getCurrentTruck as any;
			return truck.site?.opening_hours;
		},

		blockedSlotIndexes(): BlockedSlotIndexes | null {
			/**
			 * Calculates all blocked slots based on opening hours for all calendar pages
			 *
			 * This method has been enhanced to be recursive and works for all weeks,
			 * not just the initially visible ones. It:
			 *
			 * 1. Calculates the base non-working slots for a single week based on opening hours
			 * 2. Determines how many weeks need to be generated based on the current page number
			 * 3. Generates blocked slots for all necessary weeks by applying offsets to the base slots
			 * 4. Updates as the user navigates between calendar pages (via pageNumber watcher)
			 *
			 * The result is that non-working slots will properly appear on all calendar pages,
			 * regardless of how far the user navigates.
			 */
			const blockedSlots: number[] = [];
			const slotRanges = ['00:00-05:59', '06:00-11:59', '12:00-17:59', '18:00-23:59'];
			// Using standard weekdays array starting with Monday (backend data is ordered this way)
			const weekDays: any = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
			let nonWorkingDays: any = {};

			try {
				// Check if truck has site and opening hours data
				const hasSiteData = this.isSingleTruck
					? !!(this.singleTruckData?.site && typeof this.singleTruckData.site !== 'string' && this.singleTruckData.site.opening_hours)
					: this.getCurrentTruck && (this.getCurrentTruck as any).site && (this.getCurrentTruck as any).site.opening_hours;

				// If no opening hours data, return an empty array of blocked slots (meaning all slots are available)
				if (!hasSiteData) {
					// Return a data structure with no blocked slots
					const getTruckId = this.isSingleTruck ? this.truck?.id : this.getCurrentTruck ? (this.getCurrentTruck as any).id : null;

					return {
						truck_id: getTruckId,
						non_working_slots: [] // Empty array means no blocked slots
					} as BlockedSlotIndexes;
				}

				// Otherwise, process opening hours as usual
				if (this.isSingleTruck) {
					// @ts-ignore
					nonWorkingDays = this.singleTruckData?.site && typeof this.singleTruckData.site !== 'string' ? this.singleTruckData.site.opening_hours || {} : {};
				} else {
					nonWorkingDays = this.getWorkingHours || {};
				}

				// Get today's day of the week (0=Sunday, 1=Monday, ..., 6=Saturday)
				const today = new Date().getDay();
				// Convert from Sunday=0 to Monday=0 format to match our backend data
				const todayBackendIndex = today === 0 ? 6 : today - 1;

				weekDays.forEach((day: any, dayIndex: number) => {
					const date = this.currentWeekDates[dayIndex]?.slice(0, 10);
					let hours: any;

					if (this.isSingleTruck) {
						// @ts-ignore
						hours = this.singleTruckData?.site && typeof this.singleTruckData.site !== 'string' ? this.singleTruckData.site.opening_hours?.[day] || null : null;
					} else {
						hours = nonWorkingDays[day] || null;
					}

					// --- Check for override for this date ---
					const overrides = nonWorkingDays.overrides || [];
					const override = overrides.find((o: any) => {
						const d = new Date(date);
						const start = new Date(o.day_start);
						const end = new Date(o.day_end);
						return d >= start && d <= end;
					});

					if (override) {
						hours = {
							open: override.open,
							start: override.open ? override.hours_start : null,
							end: override.open ? override.hours_end : null
						};
					}

					// Calculate the offset in the calendar grid - how many days from "today" is this day?
					// We need to account for days wrapping around (e.g., if today is Friday, Sunday is +2 days)
					let daysFromToday = dayIndex - todayBackendIndex;
					if (daysFromToday < 0) {
						daysFromToday += 7; // Adjust for wrapping around to next week
					}

					// If the whole day is closed, block all slots
					if (!hours || !hours.open) {
						for (let slot = 0; slot < 4; slot++) {
							// Adjust the slot index calculation for calendar view
							// Calendar now starts with today at index 0
							const calendarDayIndex = daysFromToday;

							blockedSlots.push(calendarDayIndex * 4 + slot);
						}
						// Skip the rest of this iteration and continue with the next day
						// 'return' inside a forEach callback only exits that iteration, not the whole function
						return;
					}

					slotRanges.forEach((slotRange, slotIndex) => {
						const [slotStart, slotEnd] = slotRange.split('-');

						const start = hours.start ? parseInt(hours.start.replace(':', ''), 10) : null;
						const end = hours.end ? parseInt(hours.end.replace(':', ''), 10) : null;
						const slotStartNum = parseInt(slotStart.replace(':', ''), 10);
						const slotEndNum = parseInt(slotEnd.replace(':', ''), 10);

						// Determine if this slot should be blocked
						let shouldBlock = false;

						// Block if the slot ENDS before the store opens
						if (start !== null && slotEndNum <= start) {
							shouldBlock = true;
						}

						// Block if the slot STARTS after the store closes
						if (end !== null && slotStartNum >= end) {
							shouldBlock = true;
						}

						// Block if the slot OVERLAPS with closing time
						if (end !== null && slotStartNum < end && slotEndNum > end) {
							shouldBlock = true;
						}

						// If this slot should be blocked, add it to blockedSlots with correct calendar index
						if (shouldBlock) {
							// Use days from today as the calendar day index
							const slotCalendarDayIndex = daysFromToday;

							blockedSlots.push(slotCalendarDayIndex * 4 + slotIndex);
						}
					});
				});

				// Create base blocked slots for the current week (week 0)
				const baseBlockedSlots = [...blockedSlots];

				// Get truck ID
				let getTruckId;
				if (this.isSingleTruck) {
					getTruckId = this.truck?.id;
				} else {
					getTruckId = (this.getCurrentTruck as { id: string | number }).id;
				}

				// Calculate all the weeks we need to generate slots for
				// - Always include previous, current, and next week
				// - Include additional weeks based on the page number
				const weeksToGenerate = [-1, 0, 1, 2]; // Default: previous, current, next week, and week 2

				// Add more weeks if necessary based on page number
				// pageNumber represents how many times the user has clicked "next" or "previous"
				if (Math.abs(this.pageNumber) > 2) {
					// If user has clicked "next" more than twice, we need more future weeks
					if (this.pageNumber > 2) {
						// Add weeks up to pageNumber + 2 (to have a buffer)
						for (let i = 3; i <= this.pageNumber + 2; i++) {
							weeksToGenerate.push(i);
						}
					} else if (this.pageNumber < -2) {
						// If user has clicked "previous" more than twice, we need more past weeks
						for (let i = -3; i >= this.pageNumber - 2; i--) {
							weeksToGenerate.push(i);
						}
					}
				}

				// Generate all blocked slots for all required weeks
				const allBlockedSlots: number[] = [];

				weeksToGenerate.forEach((weekOffset) => {
					// For each week, add the base slots with the appropriate offset
					const weekSlots = baseBlockedSlots.map((slot) => slot + 28 * weekOffset);
					allBlockedSlots.push(...weekSlots);
				});

				const data: BlockedSlotIndexes = {
					truck_id: getTruckId,
					non_working_slots: allBlockedSlots
				};

				return data;
			} catch (error) {
				console.error('Error calculating blocked slots:', error);
				return null;
			}
		}
	},

	watch: {
		truckId() {
			if (this.hideCycling) {
				this.clearCalendarSlots();
			}
			this.offtime_selected = false;
			this.peaktime_selected = false;
			this.peaktime2_selected = false;
		},

		truckQuery() {
			if (!this.truckQuery) {
				// edge case fix (opening a truck with QRcode then closing it and opening another one would keep calendar_auto_select to true without watching the prop)
				this.calendar_auto_select = false;
			}
		},

		timePicked() {
			// Important to reset calendar when times are chosen in the timepicker.
			this.clearCalendarSlots();
		},

		getCurrentTruck: {
			handler(newVal, oldVal) {
				if (!this.isSingleTruck) {
					if (newVal !== oldVal) {
						this.truck = newVal;
						this.$emit('currentTruck', this.truck);
					}
				}
			},
			deep: true,
			immediate: true
		},

		updateTruckImg: {
			handler(newVal, oldVal) {
				if (!this.isSingleTruck) {
					if (newVal !== oldVal) {
						const singleTruck = this.allTrucksInDialog.find((item) => item.id === newVal) || null;
						const singleImage = singleTruck?.pictures.mockup_side;

						if (singleImage) {
							this.$emit('imageUpdate', singleImage);
						} else {
							this.$emit('imageUpdate', '');
						}
					}
				}
			},
			immediate: true
		},

		showDetailsTab: {
			handler(newVal: any, _oldVal: any) {
				if (newVal) {
					this.tab = 1;
				}
			}
		},

		pageNumber: {
			handler(newVal: number, oldVal: number) {
				if (newVal !== oldVal) {
					// Force recalculation of blocked slots when page number changes
					this.$nextTick(() => {
						// Notify the slot grid component that the blocked slots may have changed
						const slotGrid = this.$refs.slotGrid as any;
						if (slotGrid && typeof slotGrid.updateNonWorkingSlots === 'function') {
							slotGrid.updateNonWorkingSlots(this.blockedSlotIndexes?.non_working_slots || []);
						}
					});
				}
			}
		}
	},

	mounted() {
		if (this.isSingleTruck) {
			(this.setTruck as () => void)();
		}
		if (this.defaultToDetailsTab) {
			this.tab = 1;
		}
	},

	created() {
		if (this.truckQuery) {
			this.CART.resetDateTimePicked(); // Clear in case dateTimePicked - Messes up with the calendar.

			if (this.autoselectFirstSlots) {
				this.calendar_auto_select = true;
			}
		}
	},

	methods: {
		isInt(n: any): boolean {
			return Number(n) === n && n % 1 === 0;
		},

		setTruck(): void {
			this.truck = this.singleTruckData;
		},

		async handleSlotsSelected(payload: { start: string; end: string }): Promise<void> {
			this.rent_btn.loading = true;

			if (this.truck === null) {
				return;
			}

			// enforce timestamps to have Europe/Vienna TZ (thats what we expect for now)
			const time_start = new Date(dateToAustrianTimezone(payload.start).toISOString());
			const time_end = new Date(dateToAustrianTimezone(payload.end).toISOString());

			// Get slot information from the grid
			const slotGrid = this.$refs.slotGrid as any;
			if (!slotGrid) {
				this.rent_btn.loading = false;
				return;
			}

			// Get the non-working slots - these are slots limited by opening hours only, NOT booked slots
			const nonWorkingSlots = this.blockedSlotIndexes?.non_working_slots || [];

			// IMPORTANT FIX: Get the booked slots directly from the slot grid component's unavailable_slots
			// This is more reliable than using this.unavailableSlots which might be empty for some trucks
			let bookedSlots = Array.isArray(this.unavailableSlots) ? this.unavailableSlots : [];

			// If the bookedSlots array is empty but the slot grid has unavailable_slots, use those
			if (bookedSlots.length === 0 && slotGrid && Array.isArray(slotGrid.unavailable_slots)) {
				bookedSlots = slotGrid.unavailable_slots;
			}

			// Get the currently selected slots - use a local copy to avoid direct mutation
			const selectedSlots = [...slotGrid.selected_slots];

			// Sort the selected slots to ensure correct order
			selectedSlots.sort((a: number, b: number) => a - b);

			// Check if there are any booked slots within the range (including slots between start and end)
			const startSlot = Math.min(...selectedSlots);
			const endSlot = Math.max(...selectedSlots);

			// Create a complete array of all slots in the range, not just the selected ones
			const allSlotsInRange: number[] = [];
			for (let i = startSlot; i <= endSlot; i++) {
				allSlotsInRange.push(i);
			}

			// Check if there are any booked slots within the range
			const bookedSlotsInRange = allSlotsInRange.filter((slot) => bookedSlots.includes(slot));

			// If we have booked slots in the range
			if (bookedSlotsInRange.length > 0) {
				// Find the first booked slot
				const firstBookedSlot = Math.min(...bookedSlotsInRange);

				// Find the index of the first booked slot in the allSlotsInRange array
				const firstBookedSlotIndex = allSlotsInRange.indexOf(firstBookedSlot);

				// Check if there are any valid slots before the first booked slot
				if (firstBookedSlotIndex > 0) {
					// We have some valid slots before the first booked slot
					// Create a new selection from start up to the first booked slot (exclusive)
					const modifiedRange = allSlotsInRange.slice(0, firstBookedSlotIndex);

					// Make sure the end of the modified range is a free slot (not a non-working slot)
					while (modifiedRange.length > 0 && nonWorkingSlots.includes(modifiedRange[modifiedRange.length - 1])) {
						// The last slot is a non-working slot, remove it
						modifiedRange.pop();
					}

					// Only proceed if we have at least one valid slot left
					if (modifiedRange.length > 0) {
						// Update the slot grid with our modified range - using a safe approach
						// Wait until the next tick to avoid Vue's reactivity issues
						await this.$nextTick();

						// Create a temporary array instead of directly modifying the component's array
						const newSelection = [...modifiedRange];
						slotGrid.selected_slots = newSelection;

						// Convert to datetime strings
						const newStartTime = slotGrid.translateToDateTime(newSelection[0]);
						const newEndTime = slotGrid.translateToDateTime(newSelection[newSelection.length - 1], true);

						// Instead of recursively calling, directly get price for the modified range
						const modifiedTime_start = new Date(dateToAustrianTimezone(newStartTime).toISOString());
						const modifiedTime_end = new Date(dateToAustrianTimezone(newEndTime).toISOString());

						const modifiedResp = await TRUCKS.GET_RENT_RPICE({
							truck_id: this.truck.id,
							time_start: modifiedTime_start,
							time_end: modifiedTime_end
						});

						if (!(modifiedResp instanceof ErrorResponse)) {
							// Update price and calendar directly
							this.rent_btn.price = modifiedResp.amount;
							this.calendar = { start: newStartTime, end: newEndTime };
							this.rent_btn.loading = false;

							const tracking_payload = this.createSlotTrackingPayload();
							if (tracking_payload) {
								this.$tracking.slot_clicked(tracking_payload);
							}
							return;
						} else {
							// Handle error with the modified range
							// Clear the selection and show error
							await this.$nextTick();
							slotGrid.selected_slots = [];
							this.rent_btn.loading = false;

							// Per user request, no dialog for range adjustment
							return;
						}
					}
				}

				// Clear the selection using a safe approach
				await this.$nextTick();
				slotGrid.selected_slots = [];
				this.rent_btn.loading = false;
				return;
			}

			// Check if the selected slots have any non-working slots (opening hours limitations)
			const nonWorkingSlotsInSelection = selectedSlots.filter((slot) => nonWorkingSlots.includes(slot));
			const hasNonWorkingSlots = nonWorkingSlotsInSelection.length > 0;

			// Only try to extend if the first or last slot in the range is a non-working slot
			// This handles the case when a user selects a working slot first, then a non-working slot second
			const rangeStartSlot = Math.min(...selectedSlots);
			const rangeEndSlot = Math.max(...selectedSlots);
			const startIsNonWorking = nonWorkingSlots.includes(rangeStartSlot);
			const endIsNonWorking = nonWorkingSlots.includes(rangeEndSlot);

			if (startIsNonWorking || endIsNonWorking) {
				// Try to extend the range to include available times
				const extendedSlots = (this as any).calculateExtendedSlotsForRange(rangeStartSlot, rangeEndSlot);
				if (extendedSlots && extendedSlots.length > 0) {
					// There's a potential extended range, check if it would work
					const extStartSlot = Math.min(...extendedSlots);
					const extEndSlot = Math.max(...extendedSlots);
					const extStartTime = slotGrid.translateToDateTime(extStartSlot);
					const extEndTime = slotGrid.translateToDateTime(extEndSlot, true);

					const extTime_start = new Date(dateToAustrianTimezone(extStartTime).toISOString());
					const extTime_end = new Date(dateToAustrianTimezone(extEndTime).toISOString());

					try {
						const extResp = await TRUCKS.GET_RENT_RPICE({ truck_id: this.truck.id, time_start: extTime_start, time_end: extTime_end });

						if (!(extResp instanceof ErrorResponse)) {
							// The extended range would work! Ask the user if they want to extend
							const extendMessage = this.$t(
								'cust.calendar.extend_outside_hours_text',
								'This time slot falls outside of opening hours. Would you like to extend your booking to include available times?'
							) as string;
							const extendTitle = this.$t('cust.calendar.extend_outside_hours_title', 'Extend Booking') as string;

							const confirmExtend = await this.$dialogs.confirm(extendMessage, extendTitle, {
								right_btn_text: this.$t('cust.calendar.extend_booking', 'Extend Booking') as string,
								left_btn_text: this.$t('actions.cancel') as string
							});

							if (confirmExtend) {
								// User wants to extend the booking - set directly without recursion
								await this.$nextTick();
								slotGrid.selected_slots = [...extendedSlots];
								this.calendar = { start: extStartTime, end: extEndTime };
								this.rent_btn.price = extResp.amount;
								this.rent_btn.loading = false;

								const tracking_payload = this.createSlotTrackingPayload();
								if (tracking_payload) {
									this.$tracking.slot_clicked(tracking_payload);
								}
								return;
							} else {
								// User doesn't want to extend, clear the selection
								await this.$nextTick();
								slotGrid.selected_slots = [];
								this.rent_btn.loading = false;
								return;
							}
						} else if (extResp.message === 'CONFLICT') {
							// The extended range has conflicts (some slots already booked)
							// Offer to find a similar truck at another location
							const extConflictMsg = this.$t(
								'cust.calendar.extend_conflict_text',
								'Cannot extend your booking because some slots are already booked. Would you like to find a similar truck at a nearby location?'
							) as string;
							const extConflictTitle = this.$t('cust.calendar.extend_conflict_title', 'Cannot Extend Booking') as string;

							const wantOtherLocation = await this.$dialogs.confirm(extConflictMsg, extConflictTitle, {
								right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
								left_btn_text: this.$t('actions.cancel') as string
							});

							if (wantOtherLocation) {
								// User wants to find another location
								this.$emit('find-other-location');
							}

							// Clear the selection
							await this.$nextTick();
							slotGrid.selected_slots = [];
							this.rent_btn.loading = false;
							return;
						}
					} catch (error) {
						console.error('Error checking extended booking:', error);
					}
				} else {
					// No extension possible, show "find another location" dialog
					const noExtensionMessage = this.$t(
						'cust.calendar.no_extension_text',
						'This location is closed during the selected time. Would you like to find another location?'
					) as string;
					const noExtensionTitle = this.$t('cust.calendar.no_extension_title', 'Location Closed') as string;

					const wantOtherLocation = await this.$dialogs.confirm(noExtensionMessage, noExtensionTitle, {
						right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
						left_btn_text: this.$t('actions.cancel') as string
					});

					if (wantOtherLocation) {
						this.$emit('find-other-location');
					}

					// Clear the selection
					await this.$nextTick();
					slotGrid.selected_slots = [];
					this.rent_btn.loading = false;
					return;
				}
			}

			// If we get here, either there were no non-working slots at the edges of our selection,
			// or there was an error with the extension but we want to try the original selection anyway
			const resp = await TRUCKS.GET_RENT_RPICE({ truck_id: this.truck.id, time_start, time_end });

			// Handle specific error types related to opening hours
			if (resp instanceof ErrorResponse) {
				// Check if it's a conflict error (409)
				if (resp.message === 'CONFLICT') {
					// Conflict error means there are booked slots in the range that our frontend didn't catch
					// Alert the user about the conflict
					const conflictMsg = this.$t('cust.calendar.booking_conflict_text', 'Some of these time slots are already booked by another customer.') as string;
					const conflictTitle = this.$t('cust.calendar.booking_conflict_title', 'Booking Conflict') as string;

					const wantOtherLocation = await this.$dialogs.confirm(
						(conflictMsg + ' ' + this.$t('cust.calendar.find_other_location_prompt', 'Would you like to find a similar truck at a nearby location?')) as string,
						conflictTitle,
						{
							right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
							left_btn_text: this.$t('actions.cancel') as string
						}
					);

					if (wantOtherLocation) {
						// User wants to find another location
						this.$emit('find-other-location');
					}

					// Clear the selection
					await this.$nextTick();
					slotGrid.selected_slots = [];
					this.rent_btn.loading = false;
					return;
				}

				const openingHoursErrors = ['START_DAY_CLOSED', 'START_BEFORE_OPENING_HOURS', 'END_DAY_CLOSED', 'END_AFTER_OPENING_HOURS', 'END_BEFORE_OPENING_HOURS'];

				if (openingHoursErrors.includes(resp.message) && !hasNonWorkingSlots) {
					// If this is an opening hours related error, check if we can find a better range
					// BUT only if we haven't already tried to handle non-working slots above
					// Try to extend the range to include available times
					const extendedSlots = (this as any).calculateExtendedSlotsForRange(rangeStartSlot, rangeEndSlot);

					if (extendedSlots && extendedSlots.length > 0) {
						// There's a potential extended range, check if it would work
						const extStartSlot = Math.min(...extendedSlots);
						const extEndSlot = Math.max(...extendedSlots);
						const extStartTime = slotGrid.translateToDateTime(extStartSlot);
						const extEndTime = slotGrid.translateToDateTime(extEndSlot, true);

						const extTime_start = new Date(dateToAustrianTimezone(extStartTime).toISOString());
						const extTime_end = new Date(dateToAustrianTimezone(extEndTime).toISOString());

						try {
							const extResp = await TRUCKS.GET_RENT_RPICE({ truck_id: this.truck.id, time_start: extTime_start, time_end: extTime_end });

							if (!(extResp instanceof ErrorResponse)) {
								// The extended range would work! Ask the user if they want to extend
								const extendMessage = this.$t(
									'cust.calendar.extend_outside_hours_text',
									'This time slot falls outside of opening hours. Would you like to extend your booking to include available times?'
								) as string;
								const extendTitle = this.$t('cust.calendar.extend_outside_hours_title', 'Extend Booking') as string;

								const confirmExtend = await this.$dialogs.confirm(extendMessage, extendTitle, {
									right_btn_text: this.$t('cust.calendar.extend_booking', 'Extend Booking') as string,
									left_btn_text: this.$t('actions.cancel') as string
								});

								if (confirmExtend) {
									// User wants to extend the booking - set directly without recursion
									await this.$nextTick();
									slotGrid.selected_slots = [...extendedSlots];
									this.calendar = { start: extStartTime, end: extEndTime };
									this.rent_btn.price = extResp.amount;
									this.rent_btn.loading = false;

									const tracking_payload = this.createSlotTrackingPayload();
									if (tracking_payload) {
										this.$tracking.slot_clicked(tracking_payload);
									}
									return;
								} else {
									// User doesn't want to extend, clear the selection
									await this.$nextTick();
									slotGrid.selected_slots = [];
									this.rent_btn.loading = false;
									return;
								}
							} else if (extResp.message === 'CONFLICT') {
								// The extended range has conflicts (some slots already booked)
								// Offer to find a similar truck at another location
								const extConflictMsg = this.$t(
									'cust.calendar.extend_conflict_text',
									'Cannot extend your booking because some slots are already booked. Would you like to find a similar truck at a nearby location?'
								) as string;
								const extConflictTitle = this.$t('cust.calendar.extend_conflict_title', 'Cannot Extend Booking') as string;

								const wantOtherLocation = await this.$dialogs.confirm(extConflictMsg, extConflictTitle, {
									right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
									left_btn_text: this.$t('actions.cancel') as string
								});

								if (wantOtherLocation) {
									// User wants to find another location
									this.$emit('find-other-location');
								}

								// Clear the selection
								await this.$nextTick();
								slotGrid.selected_slots = [];
								this.rent_btn.loading = false;
								return;
							}
						} catch (error) {
							console.error('Error checking extended booking:', error);
						}
					}

					// If we couldn't extend the range or there was an error, ask if they want to find another location
					const errorTitle = this.$t('cust.calendar.booking_error', 'Booking Error') as string;
					let errorMessage = '';

					// Format the message based on the error type
					switch (resp.message) {
						case 'START_DAY_CLOSED':
							errorMessage = this.$t('cust.calendar.errors.start_day_closed', 'The pickup day is closed.') as string;
							break;
						case 'START_BEFORE_OPENING_HOURS':
							errorMessage = this.$t('cust.calendar.errors.start_before_opening', 'The selected pickup time is before opening hours.') as string;
							break;
						case 'END_DAY_CLOSED':
							errorMessage = this.$t('cust.calendar.errors.end_day_closed', 'The return day is closed.') as string;
							break;
						case 'END_AFTER_OPENING_HOURS':
							errorMessage = this.$t('cust.calendar.errors.end_after_opening', 'The selected return time is after closing hours.') as string;
							break;
						case 'END_BEFORE_OPENING_HOURS':
							errorMessage = this.$t('cust.calendar.errors.end_before_opening', 'The selected return time is before opening hours.') as string;
							break;
						default:
							errorMessage = this.$t('cust.calendar.errors.general', 'There was an error with your booking. Please select a different time.') as string;
					}

					// Ask if they want to find a nearby location
					const otherLocationPrompt = this.$t('cust.calendar.find_other_location_prompt', 'Would you like to find a similar truck at a nearby location?') as string;

					const wantOtherLocation = await this.$dialogs.confirm(`${errorMessage} ${otherLocationPrompt}`, errorTitle, {
						right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
						left_btn_text: this.$t('actions.cancel') as string
					});

					if (wantOtherLocation) {
						// User wants to find another location
						this.$emit('find-other-location');
					}

					// Clear the selection
					await this.$nextTick();
					slotGrid.selected_slots = [];
					this.rent_btn.loading = false;
					return;
				} else if (this.errorMsgs.includes(resp.message)) {
					// Handle other known errors
					this.$emit('openSelectionErrorDialog', { showSlotsDialog: true, slotsErrorMsg: resp.message });

					// Clear the selection
					await this.$nextTick();
					slotGrid.selected_slots = [];
					this.rent_btn.loading = false;
					return;
				}

				// For any other errors, display a generic error and offer to find another location
				const errorMsg = this.$t('cust.calendar.general_error_text', 'An error occurred while processing your booking.') as string;
				const errorTitle = this.$t('cust.calendar.general_error_title', 'Booking Error') as string;
				const otherLocationPrompt = this.$t('cust.calendar.find_other_location_prompt', 'Would you like to find a similar truck at a nearby location?') as string;

				const wantOtherLocation = await this.$dialogs.confirm(`${errorMsg} ${otherLocationPrompt}`, errorTitle, {
					right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
					left_btn_text: this.$t('actions.cancel') as string
				});

				if (wantOtherLocation) {
					// User wants to find another location
					this.$emit('find-other-location');
				}

				// Clear the selection
				await this.$nextTick();
				slotGrid.selected_slots = [];
				this.rent_btn.loading = false;
				return;
			}

			// If we get a successful response, update the price and calendar
			if (!(resp instanceof ErrorResponse)) {
				this.rent_btn.price = resp.amount;
				this.calendar = payload;
				this.rent_btn.loading = false;

				// Create tracking payload
				const tracking_payload = this.createSlotTrackingPayload();
				if (tracking_payload) {
					this.$tracking.slot_clicked(tracking_payload);
				}
			} else {
				// Just to ensure the loading state is reset if we hit any unhandled error condition
				this.rent_btn.loading = false;
			}
		},

		updateNextPage() {
			this.currentPage = 'next';
			this.pageNumber = this.pageNumber + 1;
			// Force recalculation of blocked slots by triggering a change in the component
			this.$nextTick(() => {
				// Notify the slot grid component that the blocked slots may have changed
				const slotGrid = this.$refs.slotGrid as any;
				if (slotGrid && typeof slotGrid.updateNonWorkingSlots === 'function') {
					slotGrid.updateNonWorkingSlots(this.blockedSlotIndexes?.non_working_slots || []);
				}
			});
		},

		updatePrevPage() {
			this.currentPage = 'prev';
			this.pageNumber = this.pageNumber - 1;
			// Force recalculation of blocked slots by triggering a change in the component
			this.$nextTick(() => {
				// Notify the slot grid component that the blocked slots may have changed
				const slotGrid = this.$refs.slotGrid as any;
				if (slotGrid && typeof slotGrid.updateNonWorkingSlots === 'function') {
					slotGrid.updateNonWorkingSlots(this.blockedSlotIndexes?.non_working_slots || []);
				}
			});
		},

		updateWeekDates(currentWeekDates: any) {
			this.currentWeekDates = currentWeekDates;
		},

		triggerTruckClickEvent(val: any) {
			if (!this.truck || !this.truck.id || !this.truck.site) {
				return;
			}

			const [page, page_num] = this.truckCounter ? this.truckCounter.split('/') : [1, 1];

			const payload = {
				truck_id: this.truck.id,
				site_id: this.truckLocationId,
				lat: (this.truck.site as Site)?.lat ?? 0,
				lng: (this.truck.site as Site)?.lng ?? 0,
				page: +page,
				num_pages: +page_num,
				blocked_slots: val.blocked_slots,
				available_slots: val.available_slots
			} as ITruckClickPayLoad;

			this.$tracking.truck_click(payload);
		},

		emitCarouselData(val: any) {
			this.$emit('carouselData', val);
		},

		createSlotTrackingPayload(): ISlotCLickedOrConfirmedPayload | null {
			if (!this.truck) {
				return null;
			}
			return {
				truck_id: this.truckId,
				site_id: this.truckLocationId as string,
				price: this.rent_btn.price,
				time_start: this.calendar.start,
				time_end: this.calendar.end,
				truck_size: (this.truck.model as Model).size_group
			};
		},

		takeCalendarSlots(): void {
			// Get current truck with opening hours
			let truckData: any = null;

			if (this.isSingleTruck && this.singleTruckData) {
				truckData = this.singleTruckData;
			} else if (this.getCurrentTruck) {
				truckData = this.getCurrentTruck;
			}

			// Create a valid IDateTimeSearch object
			const dateTimeSearch = new IDateTimeSearch();
			dateTimeSearch.start = this.calendar.start;
			dateTimeSearch.end = this.calendar.end;

			// Add truck data if available
			if (truckData) {
				dateTimeSearch.truck = truckData;
			}

			// Store the last date/times searched - If a booking exists it will update it with the new dates.
			this.CART.STORE_DATE_TIME_PICKED(dateTimeSearch);

			// Trigger slot_confirm tracking
			const payload = this.createSlotTrackingPayload();
			if (payload) {
				this.$tracking.slots_confirm(payload);
			}

			const currentUrl = window.location.href;
			const partnerName = this.checkUrlForPartners(currentUrl);

			if (partnerName) {
				this.CART.addPartnerName(partnerName);
			} else {
				this.CART.addPartnerName('');
			}

			this.toNextStep();
		},

		checkUrlForPartners(currentUrl: string): string | null {
			const partners = this.MISC_DATA.partners;

			if (partners.length > 0) {
				for (const partner of partners) {
					if (Array.isArray(partner.url)) {
						const match = partner.url.find((partnerUrl) => currentUrl.includes(partnerUrl));
						if (match) {
							return match;
						}
					}
				}
			}

			return null;
		},

		clearCalendarSlots(): void {
			this.calendar.start = '';
			this.calendar.end = '';
			this.offtime_selected = false;
			this.peaktime_selected = false;
			this.peaktime2_selected = false;
		},

		toNextStep(): void {
			// this.loader = true;
			// Basically move on to upsells 1 sending back the truck id - Date times are stored in CART at this point
			// Have to emit to parent here because of dialog query.
			this.$emit('to-upsells', this.truck?.id);
		},

		showAddress() {
			this.tab = 1;
		},

		copyContent() {
			// @ts-ignore
			const currentAddress = this.truckAddress || this.singleTruckData?.site?.address;
			navigator.clipboard.writeText(currentAddress);
			this.$store.commit('showSnackbar', {
				content: this.$t('cust.alert.address_copied'),
				type: 'success',
				time: 1500,
				position: 'center'
			});
		},

		openCopyAddressSection() {
			this.isOpen = !this.isOpen;
			if (this.isOpen) {
				this.copyContent();
			}
		},

		async handleNonWorkingSlotClicked(index: number) {
			// Check if the slot is actually a non-working slot
			const nonWorkingSlots = this.blockedSlotIndexes?.non_working_slots || [];
			if (!Array.isArray(nonWorkingSlots) || !nonWorkingSlots.includes(index)) {
				return;
			}

			// Get currently selected slots
			const slotGrid = this.$refs.slotGrid as any;
			if (!slotGrid) {
				return;
			}

			// Check if we have slots already selected
			const selectedSlots = [...slotGrid.selected_slots];

			// IMPORTANT FIX: Get the booked slots directly from the slot grid component's unavailable_slots
			// This is more reliable than using this.unavailableSlots which might be empty for some trucks
			let bookedSlots = Array.isArray(this.unavailableSlots) ? this.unavailableSlots : [];

			// If the bookedSlots array is empty but the slot grid has unavailable_slots, use those
			if (bookedSlots.length === 0 && slotGrid && Array.isArray(slotGrid.unavailable_slots)) {
				bookedSlots = slotGrid.unavailable_slots;
			}

			// If there are already selected slots, check if this click would create a range
			if (selectedSlots.length > 0) {
				// Sort the selected slots to get the first one
				selectedSlots.sort((a: number, b: number) => a - b);
				const startSlot = selectedSlots[0];

				// Calculate an extended range between the selected slot and this non-working slot
				const extendedSlots = (this as any).calculateExtendedSlotsForRange(startSlot, index);
				if (extendedSlots && extendedSlots.length > 0) {
					// We have a valid extension, but we need to check if there are any booked slots in the range
					// because we don't want to show the extension dialog if it would include booked slots
					const bookedSlotsInRange = extendedSlots.filter((slot: number) => Array.isArray(bookedSlots) && bookedSlots.includes(slot));
					// If there are no booked slots in the range, we can offer the extension
					if (bookedSlotsInRange.length === 0) {
						// Convert to datetime strings
						const extStartSlot = Math.min(...extendedSlots);
						const extEndSlot = Math.max(...extendedSlots);
						const extStartTime = slotGrid.translateToDateTime(extStartSlot);
						const extEndTime = slotGrid.translateToDateTime(extEndSlot, true);

						// Try to get a price for the extended range
						if (this.truck) {
							const time_start = new Date(dateToAustrianTimezone(extStartTime).toISOString());
							const time_end = new Date(dateToAustrianTimezone(extEndTime).toISOString());

							try {
								// Check if this extended booking would work
								const resp = await TRUCKS.GET_RENT_RPICE({ truck_id: this.truck.id, time_start, time_end });

								// Debug logging - Response
								console.log(`[${this.truckId}] API response:`, resp);
								console.log(`[${this.truckId}] API parameters:`, {
									truck_id: this.truck.id,
									time_start: time_start.toISOString(),
									time_end: time_end.toISOString()
								});
								// Debug logging - End

								if (resp instanceof ErrorResponse) {
									// Error getting price, check if it's because of a booking conflict
									if (resp.message === 'BOOKED_SLOTS') {
										// Debug logging - Booking conflict
										console.log(`[${this.truckId}] ERROR: BOOKED_SLOTS - This is expected if there's a booking conflict`);
										// Debug logging - End

										// Clear the selection
										await this.$nextTick();
										slotGrid.selected_slots = [];
										this.rent_btn.loading = false;
										return;
									} else if (resp.message === 'CONFLICT') {
										// Debug logging - Conflict error
										console.log(`[${this.truckId}] ERROR: CONFLICT - Already booked`);
										// Debug logging - End

										// Handle conflict errors (already booked by someone else)
										this.openingHoursDialogCannotExtend = true;
										return;
									} else if (this.errorMsgs.includes(resp.message)) {
										// Handle other known errors
										// Debug logging - Other known error
										console.log(`[${this.truckId}] ERROR: ${resp.message} - Known error type`);
										// Debug logging - End

										this.$emit('openSelectionErrorDialog', { showSlotsDialog: true, slotsErrorMsg: resp.message });

										// Clear the selection
										await this.$nextTick();
										slotGrid.selected_slots = [];
										this.rent_btn.loading = false;
										return;
									}

									// Debug logging - Generic error
									console.log(`[${this.truckId}] ERROR: Generic error - showing dialog`);
									// Debug logging - End

									// For any other errors, display a generic error and offer to find another location
									const errorMsg = this.$t('cust.calendar.general_error_text', 'An error occurred while processing your booking.') as string;
									const errorTitle = this.$t('cust.calendar.general_error_title', 'Booking Error') as string;
									const otherLocationPrompt = this.$t(
										'cust.calendar.find_other_location_prompt',
										'Would you like to find a similar truck at a nearby location?'
									) as string;

									const wantOtherLocation = await this.$dialogs.confirm(`${errorMsg} ${otherLocationPrompt}`, errorTitle, {
										right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
										left_btn_text: this.$t('actions.cancel') as string
									});

									if (wantOtherLocation) {
										// User wants to find another location
										this.$emit('find-other-location');
									}

									// Clear the selection
									await this.$nextTick();
									slotGrid.selected_slots = [];
									this.rent_btn.loading = false;
									return;
								}

								// If we get a successful response, update the price and calendar
								// Debug logging - Successful response
								console.log(`[${this.truckId}] SUCCESS: Got price, updating UI`);
								// Debug logging - End

								// Store the extended slots data for later use
								this.extendedSlotsData = {
									slots: extendedSlots,
									startTime: extStartTime,
									endTime: extEndTime,
									price: resp.amount
								};

								// Set calendar and show dialog for extending booking
								this.rent_btn.price = resp.amount;
								this.calendar = {
									start: this.extendedSlotsData.startTime,
									end: this.extendedSlotsData.endTime
								};
								this.rent_btn.loading = false;

								const tracking_payload = this.createSlotTrackingPayload();
								if (tracking_payload) {
									this.$tracking.slot_clicked(tracking_payload);
								}

								// Show dialog for extending booking
								this.openingHoursDialogExtend = true;
								return;
							} catch (error) {
								console.error('Error checking extended booking:', error);
							}
						}
					}
				}

				// If no valid extension found or there was an API error
				// Show our custom dialog for cannot extend booking
				this.openingHoursDialogCannotExtend = true;
				return;
			} else if (slotGrid.selected_slots.length > 1) {
				// If there's already a multi-slot range selected, clear it
				// This allows the user to start fresh when they click a non-working slot
				await this.$nextTick();
				slotGrid.selected_slots = [];
			}

			// Single non-working slot clicked, or we've cleared the selection above
			// Try to determine the possible extended booking range for a single non-working slot
			const extendedSlots = (this as any).calculateExtendedSlots(index);

			if (extendedSlots && extendedSlots.length > 0) {
				const bookedSlotsInRange = extendedSlots.filter((slot: number) => bookedSlots.includes(slot));
				// If there are no booked slots in the range, we can offer the extension
				if (bookedSlotsInRange.length === 0) {
					// Convert the slots to timestamps
					const startSlot = Math.min(...extendedSlots);
					const endSlot = Math.max(...extendedSlots);

					// Convert to datetime strings to call the API
					const startTime = slotGrid.translateToDateTime(startSlot);
					const endTime = slotGrid.translateToDateTime(endSlot, true);

					// Try to get a price for the extended range
					if (this.truck) {
						const time_start = new Date(dateToAustrianTimezone(startTime).toISOString());
						const time_end = new Date(dateToAustrianTimezone(endTime).toISOString());

						try {
							// Check if this extended booking would work
							const resp = await TRUCKS.GET_RENT_RPICE({ truck_id: this.truck.id, time_start, time_end });

							// Debug logging - Response
							console.log(`[${this.truckId}] API response:`, resp);
							console.log(`[${this.truckId}] API parameters:`, {
								truck_id: this.truck.id,
								time_start: time_start.toISOString(),
								time_end: time_end.toISOString()
							});
							// Debug logging - End

							if (resp instanceof ErrorResponse) {
								// Error getting price, check if it's because of a booking conflict
								if (resp.message === 'BOOKED_SLOTS') {
									// Debug logging - Booking conflict
									console.log(`[${this.truckId}] ERROR: BOOKED_SLOTS - This is expected if there's a booking conflict`);
									// Debug logging - End

									// Clear the selection
									await this.$nextTick();
									slotGrid.selected_slots = [];
									this.rent_btn.loading = false;
									return;
								} else if (this.errorMsgs.includes(resp.message)) {
									// Handle other known errors
									// Debug logging - Other known error
									console.log(`[${this.truckId}] ERROR: ${resp.message} - Known error type`);
									// Debug logging - End

									this.$emit('openSelectionErrorDialog', { showSlotsDialog: true, slotsErrorMsg: resp.message });

									// Clear the selection
									await this.$nextTick();
									slotGrid.selected_slots = [];
									this.rent_btn.loading = false;
									return;
								}

								// Debug logging - Generic error
								console.log(`[${this.truckId}] ERROR: Generic error - showing dialog`);
								// Debug logging - End

								// For any other errors, display a generic error and offer to find another location
								const errorMsg = this.$t('cust.calendar.general_error_text', 'An error occurred while processing your booking.') as string;
								const errorTitle = this.$t('cust.calendar.general_error_title', 'Booking Error') as string;
								const otherLocationPrompt = this.$t(
									'cust.calendar.find_other_location_prompt',
									'Would you like to find a similar truck at a nearby location?'
								) as string;

								const wantOtherLocation = await this.$dialogs.confirm(`${errorMsg} ${otherLocationPrompt}`, errorTitle, {
									right_btn_text: this.$t('cust.calendar.show_other_location', 'Find Another Location') as string,
									left_btn_text: this.$t('actions.cancel') as string
								});

								if (wantOtherLocation) {
									// User wants to find another location
									this.$emit('find-other-location');
								}

								// Clear the selection
								await this.$nextTick();
								slotGrid.selected_slots = [];
								this.rent_btn.loading = false;
								return;
							}

							// If we get a successful response, update the price and calendar
							// Debug logging - Successful response
							console.log(`[${this.truckId}] SUCCESS: Got price, updating UI`);
							// Debug logging - End

							// Store the extended slots data for later use
							this.extendedSlotsData = {
								slots: extendedSlots,
								startTime: startTime,
								endTime: endTime,
								price: resp.amount
							};

							// Set calendar and show dialog for extending booking
							this.rent_btn.price = resp.amount;
							this.calendar = {
								start: startTime,
								end: endTime
							};
							this.rent_btn.loading = false;

							const tracking_payload = this.createSlotTrackingPayload();
							if (tracking_payload) {
								this.$tracking.slot_clicked(tracking_payload);
							}

							// Show dialog for extending booking
							this.openingHoursDialogExtend = true;
							return;
						} catch (error) {
							console.error('Error checking extended booking:', error);
						}
					} else {
						// There are booked slots in the range, we can't offer the extension
						this.openingHoursDialogCannotExtend = true;
						return;
					}
				}

				// Either no valid extension found or an error occurred
				// Show cannot extend dialog
				this.openingHoursDialogCannotExtend = true;
			}

			// Either no valid extension found or an error occurred
			// Show cannot extend dialog
			this.openingHoursDialogCannotExtend = true;
		},

		calculateExtendedSlotsForRange(start: number, end: number): number[] | null {
			// Ensure start is always the smaller number
			if (start > end) {
				[start, end] = [end, start];
			}

			// Debug logging
			console.log(`[${this.truckId}] --- CALCULATE EXTENDED SLOTS FOR RANGE ---`);
			console.log(`[${this.truckId}] Start:`, start);
			console.log(`[${this.truckId}] End:`, end);
			// Debug logging - End

			// Non-working slots are slots limited by opening hours, not booked slots
			const nonWorkingSlots = this.blockedSlotIndexes?.non_working_slots || [];
			// Already booked slots that are unavailable
			const bookedSlots = Array.isArray(this.unavailableSlots) ? this.unavailableSlots : [];

			// Debug logging - Slots status
			console.log(`[${this.truckId}] Booked slots:`, bookedSlots);
			console.log(`[${this.truckId}] Non-working slots:`, nonWorkingSlots);
			// Debug logging - End

			// If either the start or end is a non-working slot (opening hours limited), try to extend the range
			const startIsNonWorking = Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(start);
			const endIsNonWorking = Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(end);

			// Find possible extended start
			let extendedStart = start;
			if (startIsNonWorking) {
				// Look backward up to 8 slots to find a regular working slot
				for (let i = 1; i <= 8; i++) {
					const slot = start - i;
					if (Array.isArray(bookedSlots) && bookedSlots.includes(slot)) {
						break; // Stop at booked slots
					}

					// If this is a valid slot (not a non-working slot), update the extended start
					if (!(Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(slot))) {
						extendedStart = slot;
						break;
					}
				}
			}

			// Find possible extended end
			let extendedEnd = end;
			if (endIsNonWorking) {
				// Look forward up to 8 slots to find a regular working slot
				for (let i = 1; i <= 8; i++) {
					const slot = end + i;
					if (Array.isArray(bookedSlots) && bookedSlots.includes(slot)) {
						break; // Stop at booked slots
					}

					// If this is a valid slot (not a non-working slot), update the extended end
					if (!(Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(slot))) {
						extendedEnd = slot;
						break;
					}
				}
			}

			// If we couldn't find valid slots in at least one direction when needed, we can't extend
			if ((startIsNonWorking && extendedStart === start) || (endIsNonWorking && extendedEnd === end)) {
				return null;
			}

			// Create the complete range of slots from extended start to extended end
			const extendedSlots: number[] = [];
			for (let slot = extendedStart; slot <= extendedEnd; slot++) {
				extendedSlots.push(slot);
			}

			// Check if any of the slots in our range are available (not non-working)
			const hasAvailableSlot = extendedSlots.some((slot) => !(Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(slot)));

			// If no slots are available, this range can't be extended
			if (!hasAvailableSlot) {
				return null;
			}

			return extendedSlots;
		},

		// Button actions for the new dialogs
		extendBooking() {
			// Close the dialog
			this.openingHoursDialogExtend = false;

			// Use the stored extended slots data to set the booking
			if (this.extendedSlotsData) {
				const slotGrid = this.$refs.slotGrid as any;
				if (slotGrid) {
					// Set the selected slots to the extended range
					slotGrid.selected_slots = [...this.extendedSlotsData.slots];

					// Set calendar and price directly
					this.calendar = {
						start: this.extendedSlotsData.startTime,
						end: this.extendedSlotsData.endTime
					};
					this.rent_btn.price = this.extendedSlotsData.price;
					this.rent_btn.loading = false;

					// Track the selection
					const tracking_payload = this.createSlotTrackingPayload();
					if (tracking_payload) {
						this.$tracking.slot_clicked(tracking_payload);
					}
				}
			}
		},

		findNearbyLocation() {
			// Close both dialogs
			this.openingHoursDialogExtend = false;
			this.openingHoursDialogCannotExtend = false;

			// Make sure we have a location to pass to the map
			let truckLocation = null;
			if (this.singleTruckData && this.singleTruckData.position) {
				truckLocation = {
					lat: this.singleTruckData.position.lat,
					lng: this.singleTruckData.position.lng,
					site_id: this.singleTruckData.position.site_id
				};
			} else if (this.singleTruckData?.site && typeof this.singleTruckData.site !== 'string') {
				truckLocation = {
					lat: this.singleTruckData.site.lat,
					lng: this.singleTruckData.site.lng,
					site_id: this.singleTruckData.site.id
				};
			}

			// Send the event
			EventBus.$emit('map:find-other-location', truckLocation);
		},

		cancelSelection() {
			// Close the dialog
			this.openingHoursDialogCannotExtend = false;
			this.openingHoursDialogExtend = false;

			// Clear the selection
			const slotGrid = this.$refs.slotGrid as any;
			if (slotGrid) {
				slotGrid.selected_slots = [];
			}
		},

		cancelExtendDialog() {
			// Close the dialog
			this.openingHoursDialogExtend = false;

			// Clear the selection
			const slotGrid = this.$refs.slotGrid as any;
			if (slotGrid) {
				slotGrid.selected_slots = [];
			}
		},

		calculateExtendedSlots(index: number): number[] | null {
			// Non-working slots are slots limited by opening hours, not booked slots
			const nonWorkingSlots = this.blockedSlotIndexes?.non_working_slots || [];
			// Already booked slots that are unavailable
			const bookedSlots = Array.isArray(this.unavailableSlots) ? this.unavailableSlots : [];

			// Debug logging
			console.log(`[${this.truckId}] --- CALCULATE EXTENDED SLOTS ---`);
			console.log(`[${this.truckId}] Slot index:`, index);
			console.log(`[${this.truckId}] Booked slots:`, bookedSlots);
			console.log(`[${this.truckId}] Non-working slots:`, nonWorkingSlots);
			// Debug logging - End

			// If this isn't a non-working slot, we can't extend it
			if (!Array.isArray(nonWorkingSlots) || !nonWorkingSlots.includes(index)) {
				return null;
			}

			// Look back up to 8 slots to find the nearest valid slot
			let firstValidSlot = null;
			for (let i = 1; i <= 8; i++) {
				const slot = index - i;

				// Stop if we hit a booked slot (not available for booking)
				if (Array.isArray(bookedSlots) && bookedSlots.includes(slot)) {
					break;
				}

				// If we found a working slot (not a non-working slot), remember it and stop looking
				if (!(Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(slot))) {
					firstValidSlot = slot;
					break;
				}
			}

			// Look ahead up to 8 slots to find the nearest valid slot
			let lastValidSlot = null;
			for (let i = 1; i <= 8; i++) {
				const slot = index + i;

				// Stop if we hit a booked slot (not available for booking)
				if (Array.isArray(bookedSlots) && bookedSlots.includes(slot)) {
					break;
				}

				// If we found a working slot (not a non-working slot), remember it and stop looking
				if (!(Array.isArray(nonWorkingSlots) && nonWorkingSlots.includes(slot))) {
					lastValidSlot = slot;
					break;
				}
			}

			// If we didn't find valid slots in either direction, we can't extend
			if (firstValidSlot === null || lastValidSlot === null) {
				return null;
			}

			// Create the complete range of slots from first to last valid slot
			const allSlots: number[] = [];
			for (let slot = firstValidSlot; slot <= lastValidSlot; slot++) {
				allSlots.push(slot);
			}

			return allSlots;
		}
	}
});
